import { toast } from '@components/RootNotificationWrapper';
import { BaseQueryFn } from '@reduxjs/toolkit/dist/query';
import { PortalContext } from '@src/context/portal';

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useContext } from 'react';

export const PAGE_SIZE = 20;

type ErrorStructure = {
  // чтобы понимать какая конкретно ошибка и завязаться на нее для дополнительной логики (формы) - нужно в будущем дополнительное поле, но спешка спешка спешка
  detail: string;
  type: string; // не для нас
  title: string; // используем пока только этот текст для отображении ошибки, других параметров чтобы зацепиться за конкретность ошибки нет, текст будет разный в зависимости от перевода
  status: number; // код ошибки, но не уникальный для каждой. т.е. стандартный http код
  instance: string; // модуль на бэке
  traceId: string; // тоже не для нас
  errors: Record<string, unknown>; // типизировать
};
const axiosBody = {
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
};
export const Axios = axios.create(axiosBody);

const axiosErrorInterceptorFunction = (error, toastEveryError) => {
  // В случае если не авторизованы делаем редирект

  if (error.response?.status === 401) {
    const redirectUrl = `${
      window.env?.REACT_APP_PASSPORT_WELCOME ?? (process.env.REACT_APP_PASSPORT_WELCOME as string)
    }?backUrl=${window.location.href}`;

    window.location.href = redirectUrl;
    return;
  }

  const formattedError = error?.response?.data as unknown as ErrorStructure;

  if ((error.response?.status === 500 || error.response?.status === 400) && formattedError.detail) {
    toast({ variant: 'error', message: formattedError.detail });
  } else if (
    error.response?.status === 400 &&
    formattedError.errors &&
    Object.values(formattedError.errors)[0] &&
    Object.values(formattedError.errors)[0]?.[0]
  ) {
    toast({ variant: 'error', message: Object.values(formattedError.errors)[0]?.[0] as string });
  } else if (toastEveryError) {
    toast({ variant: 'error', message: formattedError.detail || formattedError?.title || 'Error' });
  }
};
Axios.interceptors.response.use(
  (response: AxiosResponse<unknown>) => response,
  (error: AxiosError<any>) => {
    axiosErrorInterceptorFunction(error, true);
    return error;
  },
);

const axiosQuery =
  ({
    baseUrl,
    axiosInstance,
  }: {
    baseUrl: string;
    axiosInstance: AxiosInstance;
  }): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
      headers?: AxiosRequestConfig['headers'];
    },
    unknown,
    unknown
  > =>
  async ({ url, method = 'GET', data, params, headers }) => {
    const response = await axiosInstance({
      url: `${
        window.env?.REACT_APP_SELECTION_BASE_URL ?? process.env.REACT_APP_SELECTION_BASE_URL
      }/api/v1/${baseUrl}${url}`,
      method,
      data,
      params,
      headers,
    });
    return { data: response };
  };

const axiosPortalQuery =
  ({
    baseUrl,
    axiosInstance,
  }: {
    baseUrl: string;
    axiosInstance: AxiosInstance;
  }): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
      headers?: AxiosRequestConfig['headers'];
    },
    unknown,
    unknown
  > =>
  async ({ url, method = 'GET', data, params, headers }) => {
    const response = await axiosInstance({
      url: `/api/v1/${baseUrl}${url}`,
      method,
      data,
      params,
      headers,
    });
    return { data: response };
  };
export const axiosBaseQuery = ({ baseUrl }) => axiosQuery({ baseUrl, axiosInstance: Axios });
export const axiosPortalBaseQuery = ({ baseUrl }) => axiosPortalQuery({ baseUrl, axiosInstance: Axios });

export const onCacheEntryAdded =
  (reducerPath: string, nameMethod: string) =>
  async (arg, { cacheDataLoaded, getState, updateCachedData, cacheEntryRemoved }) => {
    await cacheDataLoaded;

    if (arg.page.number > 1) {
      const lastQueryKeyParams = JSON.stringify({
        ...arg,
        page: {
          ...arg.page,
          number: arg.page.number - 1,
        },
      });

      // Фильтры должны быть в алфавитном порядке
      const lastQueryKey = `${nameMethod}(${lastQueryKeyParams})`;

      const lastCacheEntry = getState()[reducerPath].queries[lastQueryKey]?.data;

      const cacheUpdate = state => {
        state.data?.unshift(...lastCacheEntry.data);
      };

      updateCachedData(cacheUpdate);
      await cacheEntryRemoved;
    }
  };

type PropsWithContext = {
  children: JSX.Element;
};

const timezoneOffset = -new Date().getTimezoneOffset();

export const WithContext: React.FC<PropsWithContext> = ({ children }) => {
  const { module, companyId, companyAddressId } = useContext(PortalContext);
  Axios.interceptors.request.use(request => {
    if (!request.headers) {
      request.headers;
    }
    request.headers['x-module'] = module;
    request.headers['x-company-id'] = companyId;
    request.headers['x-timezone-offset-minutes '] = timezoneOffset;
    request.headers['x-company-addr-id'] = companyAddressId;

    return request;
  });

  return children;
};
